import React, { ReactElement, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getOrders, getPickToursInProgress } from '../../../redux/actions/orders.actions';
import refreshImage from '../../../assets/img/refresh-icon.svg';
import styles from './RefreshButton.css';
import { NavMenuTab } from '../../../types';
import useAutoRefresh from '../../../hooks/useAutoRefresh';
import { getConfigAuthValuesFromState } from '../../../redux/selectors';

export interface OwnProps {
  activeNavTab: NavMenuTab;
}

export interface StateProps {
  lastAttemptSuccess: boolean;
  lastAttemptTimeStamp: number;
  pickerStoreID: string | null;
}

type Props = StateProps & OwnProps;

const DEFAULT_DELAY_TIME: number = 60000;

const RefreshButton = ({
  lastAttemptSuccess,
  lastAttemptTimeStamp,
  pickerStoreID,
  activeNavTab,
}: Props): ReactElement => {
  const [disabled, setDisabled] = useState(true);
  const [manualRefreshCount, setManualRefreshCount] = useState(0);
  const dispatch = useDispatch();
  const configAuthValues = useSelector(getConfigAuthValuesFromState);
  const delay = configAuthValues?.AUTOREFRESH_DELAY_TIME ?? DEFAULT_DELAY_TIME;

  useEffect(() => {
    setDisabled(true);
    const timeElapsed = Date.now() - lastAttemptTimeStamp;
    const timeOut = lastAttemptSuccess ? 5000 - timeElapsed : 2000 - timeElapsed;
    const timer = setTimeout(() => {
      setDisabled(false);
    }, timeOut);
    return () => clearTimeout(timer);
  }, [lastAttemptTimeStamp, lastAttemptSuccess]);

  const { t } = useTranslation();
  const translationRoot = 'HEADER';

  const refreshOrders = useCallback(() => {
    if (pickerStoreID) {
      if (activeNavTab === 'resetPickTour') {
        dispatch(getPickToursInProgress({ fulfillmentStoreID: pickerStoreID }));
      } else {
        dispatch(getOrders({ fulfillmentStoreID: pickerStoreID, refreshOrders: true }));
      }
    }
  }, [pickerStoreID, activeNavTab, dispatch]);

  const handleManualRefresh = useCallback(() => {
    refreshOrders();
    setManualRefreshCount(prev => prev + 1);
  }, [refreshOrders]);

  useAutoRefresh(
    () => {
      refreshOrders();
    },
    delay,
    manualRefreshCount
  );

  return (
    <button type="button" className={styles.container} disabled={disabled} onClick={handleManualRefresh}>
      <img src={refreshImage} alt="refresh" className={styles.refreshIcon} />
      {t(`${translationRoot}.REFRESH`)}
    </button>
  );
};

export default RefreshButton;
